import { useState } from "react";
import t from "@/lang/lang";
import { useListCompanyQuery } from "@/services/backend/company/api/company";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListRequest,
  useOmniDataSheetListQuery,
  useOmniDataSheetPatchMutation,
} from "../api/data-sheet-api";
import { DataSheet } from "../types/data-sheet";
import { DataSheetDeleteDialog } from "./data-sheet-delete-dialog";
import { DataSheetDownloadButton } from "./data-sheet-download-button";
import { DataSheetUploadDialog } from "./data-sheet-upload-dialog";

/**
 * DataSheetsCard lists the data sheets of the service kind of the
 * companies the user may see, to upload, download, deactivate and delete
 * them.
 */
export function DataSheetsCard({ serviceKind }: { serviceKind: string }) {
  const [page, setPage] = useState({ limit: 20, offset: 0 });
  // The backend requires a company filter with at least one company.
  const { data: companyList } = useListCompanyQuery({});
  const companyIds = companyList?.companies.map((c) => c.id) ?? [];
  const request: ListRequest = {
    company: { active: true, values: companyIds },
    serviceKind: { active: true, values: [serviceKind] },
    ...page,
  };
  const { data, isLoading, error } = useOmniDataSheetListQuery(request, {
    skip: companyIds.length === 0,
  });
  const emptyList = !isLoading && !error && data?.items.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Datenblätter")}</CardTitle>
        <CardDescription>
          {t(
            "PDF-Dateien zu Produkten oder Leistungen. Sie werden den Positionen zugeordnet und an die Dokumente mit Anhängen angehängt. Inaktive Datenblätter werden nicht mehr angeboten, bleiben aber an bestehenden Positionen erhalten.",
          )}
        </CardDescription>
        <CardAction>
          <DataSheetUploadDialog serviceKind={serviceKind} />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-2">
        <TableCard className="border-none shadow-none">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Aktiv")}</TableHead>
                <TableHead>{t("Bezeichnung")}</TableHead>
                <TableHead>{t("Datei")}</TableHead>
                <TableHead>{t("Unternehmen")}</TableHead>
                <TableHead>{t("Aktionen")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableLoadingRow colSpan={5} />}
              {error && <TableRtkErrorRow error={error} colSpan={5} />}
              {emptyList && <TableEmptyRow colSpan={5} />}
              {data?.items.map((dataSheet) => (
                <DataSheetRow key={dataSheet.id} dataSheet={dataSheet} />
              ))}
            </TableBody>
          </Table>
        </TableCard>
        {data && !emptyList && (
          <div className="flex items-center">
            <PaginationLimitOffset
              offset={page.offset}
              limit={page.limit}
              totalCount={data.totalCount}
              setLimitOffset={(limit, offset) => setPage({ limit, offset })}
              className="ml-auto"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DataSheetRow({ dataSheet }: { dataSheet: DataSheet }) {
  const [patch, { isLoading }] = useOmniDataSheetPatchMutation();

  return (
    <TableRow>
      <TableCell>
        <Checkbox
          className="ml-4"
          checked={dataSheet.active}
          disabled={isLoading}
          onCheckedChange={(active) =>
            patch({ id: dataSheet.id, active: active === true })
          }
          aria-label={t("Aktiv")}
        />
      </TableCell>
      <TableCell>{dataSheet.label}</TableCell>
      <TableCell>
        <DataSheetDownloadButton
          id={dataSheet.id}
          filename={dataSheet.filename}
        />
      </TableCell>
      <TableCell>
        <CompanyName companyId={dataSheet.companyId} />
      </TableCell>
      <TableCell>
        <DataSheetDeleteDialog dataSheet={dataSheet} />
      </TableCell>
    </TableRow>
  );
}
